export interface WeightOrVolume {
    value: number;
    unit: string;
}

export const parseWeightOrVolumeValue = (
    value: unknown,
): number | null => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }

    if (typeof value !== 'string') {
        return null;
    }

    const normalizedValue = value
        .trim()
        .replace(',', '.');

    if (!normalizedValue) {
        return null;
    }

    const parsedValue = Number(normalizedValue);

    return Number.isFinite(parsedValue)
        ? parsedValue
        : null;
};

export const normalizeWeightOrVolume = (
    weightOrVolume: unknown,
    legacyValue?: unknown,
    legacyUnit?: unknown,
): WeightOrVolume | null => {
    if (
        weightOrVolume &&
        typeof weightOrVolume === 'object'
    ) {
        const structuredValue =
            weightOrVolume as Record<string, unknown>;

        const value = parseWeightOrVolumeValue(
            structuredValue.value,
        );

        const unit = String(
            structuredValue.unit ?? '',
        ).trim();

        if (value !== null && unit) {
            return {
                value,
                unit,
            };
        }
    }

    const value =
        parseWeightOrVolumeValue(legacyValue);

    const unit = String(
        legacyUnit ?? '',
    ).trim();

    if (value === null || !unit) {
        return null;
    }

    return {
        value,
        unit,
    };
};

export class PlanogramProductModel {
    [key: string]: unknown;

    constructor(
        args: Record<string, unknown> = {},
    ) {
        const {
            id,
            ean,
            name,
            price,
            currency,
            hasPhoto,
            photoUrl,
            categoryName,
            categoryColor,
            mdbId,
            evaDtsId,
            weightOrVolume,
            weightVolume,
            unitOfMeasurement,
            effectiveDate,
        } = args;

        const normalizedWeightOrVolume =
            normalizeWeightOrVolume(
                weightOrVolume,
                weightVolume,
                unitOfMeasurement,
            );

        this.id = id ?? null;
        this.ean = ean ?? null;
        this.name = name || '';
        this.price = price ?? null;
        this.currency = currency ?? null;
        this.hasPhoto = hasPhoto === true;
        this.photoUrl = photoUrl ?? null;
        this.categoryName = categoryName || '';
        this.categoryColor = categoryColor || '';
        this.mdbId = mdbId || null;
        this.evaDtsId = evaDtsId || null;

        /*
         * New planogram API representation.
         */
        this.weightOrVolume =
            normalizedWeightOrVolume;

        /*
         * Legacy UI representation.
         *
         * Keep these properties until all UI components use
         * weightOrVolume directly.
         */
        this.weightVolume =
            normalizedWeightOrVolume?.value ??
            weightVolume ??
            null;

        this.unitOfMeasurement =
            normalizedWeightOrVolume?.unit ??
            unitOfMeasurement ??
            null;

        this.effectiveDate = effectiveDate
            ? effectiveDate instanceof Date
                ? effectiveDate
                : new Date(effectiveDate as string)
            : null;
    }

    static fromFormData(
        formData: Record<string, unknown>,
    ): PlanogramProductModel {
        const productData = (
            formData?.product ?? {}
        ) as Record<string, unknown>;

        const legacyWeightVolume =
            formData?.weightVolume ??
            productData?.weightVolume ??
            null;

        const legacyUnitOfMeasurement =
            formData?.unitOfMeasurement ??
            productData?.unitOfMeasurement ??
            null;

        const weightOrVolume =
            normalizeWeightOrVolume(
                formData?.weightOrVolume ??
                    productData?.weightOrVolume,
                legacyWeightVolume,
                legacyUnitOfMeasurement,
            );

        return new PlanogramProductModel({
            id:
                productData?.id ??
                productData?.externalId ??
                productData?.value ??
                formData?.productExternalId,
            ean:
                productData?.ean ??
                formData?.ean,
            name:
                productData?.name ??
                productData?.label ??
                formData?.productName ??
                '',
            price:
                formData?.price ??
                productData?.price,
            currency:
                formData?.currency ??
                productData?.currency,
            hasPhoto:
                productData?.hasPhoto === true,
            photoUrl:
                productData?.photoUrl ?? null,
            categoryName:
                productData?.categoryName,
            categoryColor:
                productData?.categoryColor,
            mdbId:
                formData?.mdbId,
            evaDtsId:
                formData?.evaDtsId,
            weightOrVolume,
            weightVolume:
                weightOrVolume?.value ??
                legacyWeightVolume,
            unitOfMeasurement:
                weightOrVolume?.unit ??
                legacyUnitOfMeasurement,
            effectiveDate:
                formData?.effectiveDate,
        });
    }

    static fromApiCell(
        cell: Record<string, unknown>,
    ): PlanogramProductModel {
        const weightOrVolume =
            normalizeWeightOrVolume(
                cell?.weightOrVolume ??
                    cell?.weight_or_volume,
                cell?.weightVolume ??
                    cell?.weight_volume,
                cell?.unitOfMeasurement ??
                    cell?.unit_of_measurement,
            );

        return new PlanogramProductModel({
            id: cell?.productExternalId,
            ean: cell?.ean,
            name: cell?.productName,
            price: cell?.price,
            currency: cell?.currency,
            hasPhoto:
                Boolean(cell?.productPhotoUrl),
            photoUrl:
                cell?.productPhotoUrl || null,
            categoryName:
                cell?.categoryName,
            categoryColor:
                cell?.categoryColor,
            mdbId:
                cell?.mdbId,
            evaDtsId:
                cell?.evaDtsId,
            effectiveDate:
                cell?.effectiveDate,
            weightOrVolume,
            weightVolume:
                weightOrVolume?.value ?? null,
            unitOfMeasurement:
                weightOrVolume?.unit ?? null,
        });
    }
}

export const createPlanogramProduct = (
    formData: Record<string, unknown>,
): PlanogramProductModel =>
    PlanogramProductModel.fromFormData(formData);